import { Component, Input, OnInit } from '@angular/core';
import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { ButtonSize, ButtonStyle } from '@constants/button/1.0';
import { LoadingColor, LoadingType } from '@constants/loading/1.0';
import { MessageType } from '@constants/message/1.0';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { TemplateResponse } from '@generated/tcea/models/template-response';
import { TenderTypeRow } from '@interfaces/aggregator/1.0';
import { TemplatesService } from '@services/templates/1.0/templates.service';
import { catchError, filter, map, Observable, of, Subject, takeUntil } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { FormCanDeactivate } from '@extensions/form-can-deactivate';
import { PermissionsService } from '@services/permissions/1.0/permissions.service';
import { PaymentConfigService } from '@services/payment-config/1.0/payment-config.service';
import {
  GenericTemplate,
  PaymentNetwork,
  TemplateType,
  TenderConfiguration,
  TenderType,
} from '@generated/tcea/models';
import { TemplateCreateRequest, Tenant } from '@generated/tcea/models';
import { BrandService } from '@services/brand/1.0/brand.service';
import { StoreResolverService } from '@app/store/services/store-resolver.service';
import { StoreService } from '@services/store/1.0/store.service';
import { Store } from '@interfaces/store/1.0';
import { AggregatorConfig } from '@interfaces/aggregator/1.0/index';
import { DeepCloneService } from '@services/utilities/1.0/deep-clone.service';
import { NgIf, NgFor } from '@angular/common';
import { MessageComponent } from '../message/1.0/message.component';
import { LoadingComponent } from '../loading/1.0/loading.component';
import { MatFormField, MatLabel, MatError } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { ButtonComponent } from '../button/1.0/button.component';
import { TxpIntegersOnlyDirective } from '../../directives/integers-only/1.0/txp-integers-only.directive';
import { MatCheckbox } from '@angular/material/checkbox';

@Component({
  selector: 'txp-tender-template',
  templateUrl: './tender-template.component.html',
  styleUrls: ['./tender-template.component.scss'],
  standalone: true,
  imports: [
    NgIf,
    MessageComponent,
    LoadingComponent,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatError,
    MatSelect,
    NgFor,
    MatOption,
    ButtonComponent,
    TxpIntegersOnlyDirective,
    MatCheckbox,
    TranslateModule,
  ],
})
export class TenderTemplateComponent extends FormCanDeactivate implements OnInit {
  @Input() storeTemplate: boolean = false;

  buttonStyle = ButtonStyle; // expose enum to template
  buttonSize = ButtonSize; // expose enum to template
  loadingColor = LoadingColor; // expose enum to template
  loadingType = LoadingType; // expose enum to template
  messageType = MessageType; // expose enum to template
  form: UntypedFormGroup;
  isLoading = false;
  showError = false;
  showLoadError = false;
  showSaveError = false;
  duplicateTenderNameError = false;
  invalidTemplateError = false;
  brandPaymentConfigReadOnly = true;
  tenderTypes: { label: string; value: string }[];
  channelsList = {};
  adapters = [];
  templateResponse: TemplateResponse;
  isSaving = false;
  brandId: string;
  brand: Tenant;
  storeTemplates: TemplateResponse[];
  store: Store;
  templateId;
  loadTemplateError = false;
  invalidFormError = false;
  channels;
  serviceTypes;
  templateTenderNames = [];
  tenderNameBeforeEdit: string;
  emptyChannelsMsg = false;
  allSelected = false;
  selectedZeroTenderIndex?: number;
  paymentNetworks = Object.keys(PaymentNetwork).filter((network) => network !== 'All');
  emptyPaymentLocations = false;
  readonly tenderTypesWithoutPaymentNetworks = [
    TenderType.Cash,
    TenderType.GooglePay,
    TenderType.GiftCard,
  ];

  private unsubscribe = new Subject<void>();

  constructor(
    private formBuilder: UntypedFormBuilder,
    private translateService: TranslateService,
    private route: ActivatedRoute,
    private router: Router,
    private paymentConfigService: PaymentConfigService,
    private templatesService: TemplatesService,
    private brandService: BrandService,
    private storeResolverService: StoreResolverService,
    private storeService: StoreService,
    private permissionsService: PermissionsService,
  ) {
    super();
    this.isLoading = true;

    this.storeResolverService.errorSubject.pipe(takeUntil(this.unsubscribe)).subscribe((error) => {
      if (error) {
        this.isLoading = false;
        this.showLoadError = true;
      }
    });
    this.brandPaymentConfigReadOnly = !this.permissionsService.getPermission(
      'payment-config.template:write',
    );
    this.tenderTypes = this.getTenderTypes();
    this.loadBrandContext();
    this.form = this.formBuilder.group({
      name: ['', this.storeTemplate ? null : Validators.required],
      template: this.formBuilder.group({
        tenderConfigurations: this.formBuilder.array([]),
        supportedChannel: this.formBuilder.array([]),
      }),
    });

    if (this.permissionsService.getPermission('payment-config.template:read')) {
      this.reset();
      this.getAdapters();
    }
  }

  get tenderConfigurations(): UntypedFormArray {
    return this.form.get('template.tenderConfigurations') as UntypedFormArray;
  }

  get supportedChannel(): UntypedFormArray {
    return this.form.get('template.supportedChannel') as UntypedFormArray;
  }

  ngOnInit() {
    if (this.storeTemplate) this.loadStoreContext();
    this.form.valueChanges.subscribe(() => {
      this.templateTenderNames = Array.from(
        new Set(this.tenderConfigurations.value.map((tender) => tender.name)),
      );
    });
  }

  loadBrandContext() {
    this.brandService.currentBrandBehaviorSubject
      .pipe(
        filter((brand) => brand.isSet),
        takeUntil(this.unsubscribe),
      )
      .subscribe((brand) => {
        if (!this.storeTemplate) {
          // this.channels = brand.aggregatorIds; future use
          this.channels = ['consumer-api'];
          this.serviceTypes = brand.supportedServiceTypes;
        }
      });
  }

  loadStoreContext() {
    this.updateNameValidator();
    this.storeTemplate &&
      this.storeResolverService.storeSubject
        .pipe(takeUntil(this.unsubscribe))
        .subscribe((store: Store) => {
          if (store) {
            this.store = store;
            this.channelsList = this.createChannelsList(
              store.aggregators?.filter((agg) => agg.aggregatorId === 'consumer-api'),
            );
            // this.channelsList = this.createChannelsList(store.aggregators); future use
            this.isLoading = false;
          } else {
            this.isLoading = false;
          }
        });
  }

  formatStoreAggregatorsToTemplate(aggregatorsData: any[]): any {
    const template: any = {
      template: {
        supportedChannel: {},
        tenderConfigurations: [],
      },
    };

    const uniqueTenderConfigurationsMap = new Map<string, TenderConfiguration>();
    aggregatorsData?.forEach((aggregator) => {
      const { aggregatorId, serviceType, tenderConfigurations } = aggregator;

      const tenderNames: string[] = [];
      const paymentLocationsSet = new Set<string>();

      tenderConfigurations?.forEach((config) => {
        tenderNames.push(config.name);

        // Collect paymentLocations from all configurations
        if (config.paymentLocations) {
          config.paymentLocations.forEach((location: string) => paymentLocationsSet.add(location));
        }

        const configKey = `${config.name}::${config.tenderType || ''}`;
        const existingConfig = uniqueTenderConfigurationsMap.get(configKey);
        if (!existingConfig || this.shouldReplaceTenderConfig(existingConfig, config)) {
          uniqueTenderConfigurationsMap.set(configKey, { ...config });
        }
      });

      if (serviceType && tenderNames.length) {
        const channelKey = `${aggregatorId} [${serviceType}]`;
        if (!template.template.supportedChannel[channelKey]) {
          template.template.supportedChannel[channelKey] = {
            tenderName: [],
            paymentLocations: [],
          };
        }

        template.template.supportedChannel[channelKey].tenderName.push(...tenderNames);
        template.template.supportedChannel[channelKey].paymentLocations =
          Array.from(paymentLocationsSet);
      }
    });
    template.template.tenderConfigurations = Array.from(uniqueTenderConfigurationsMap.values());

    return template;
  }

  private shouldReplaceTenderConfig(
    existingConfig: TenderConfiguration,
    incomingConfig: TenderConfiguration,
  ): boolean {
    const existingScore = this.getTenderConfigCompletenessScore(existingConfig);
    const incomingScore = this.getTenderConfigCompletenessScore(incomingConfig);

    return incomingScore > existingScore;
  }

  private getTenderConfigCompletenessScore(config: TenderConfiguration): number {
    const hasAdapter = !!config?.adapter;
    const hasPaymentNetworks =
      Array.isArray(config?.paymentNetworks) && config.paymentNetworks.length > 0;
    const hasPaymentLocations =
      Array.isArray(config?.paymentLocations) && config.paymentLocations.length > 0;

    return Number(hasAdapter) + Number(hasPaymentNetworks) + Number(hasPaymentLocations);
  }

  updateNameValidator(): void {
    const nameControl = this.form.get('name');
    if (nameControl) {
      if (this.storeTemplate) {
        nameControl.clearValidators();
      } else {
        nameControl.setValidators([Validators.required]);
      }

      nameControl.updateValueAndValidity();
    }
  }

  createChannelsList(data: any[]) {
    this.emptyChannelsMsg = false;
    const channelsList = {};

    data?.forEach((item) => {
      const aggregatorId = item.aggregatorId;
      const serviceType = item.serviceType;

      if (serviceType && !channelsList[aggregatorId]?.includes(serviceType)) {
        if (!channelsList[aggregatorId]) {
          channelsList[aggregatorId] = [];
        }
        channelsList[aggregatorId].push(serviceType);
      }
    });
    this.channels = Object.keys(channelsList);
    if (!!!this.channels.length || !!!Object.keys(channelsList).length) {
      this.emptyChannelsMsg = true;
    }
    return channelsList;
  }

  onChannelChange(index: number): void {
    const supportedChannelControl = this.form?.get('template.supportedChannel') as UntypedFormArray;
    const channelControl = supportedChannelControl.at(index).get('channel');
    let serviceTypesForSelection = [...this.serviceTypes];

    if (this.storeTemplate) {
      if (channelControl) {
        serviceTypesForSelection = this.channelsList[channelControl.value] || [];
      }
    }
    let allSelectedServiceTypes = [];
    this.supportedChannel.value.forEach((item) => {
      if (item.channel === channelControl.value) {
        allSelectedServiceTypes = [...allSelectedServiceTypes, ...item.serviceType];
      }
    });

    const selectedServiceTypesString = JSON.stringify(
      Object.keys(allSelectedServiceTypes.flat()).sort(),
    );
    const serviceTypesForSelectionString = JSON.stringify(
      Object.keys(serviceTypesForSelection)?.sort(),
    );

    this.allSelected = selectedServiceTypesString === serviceTypesForSelectionString;
    this.supportedChannel.value.forEach((item, idx) => {
      if (!this.allSelected) {
        let remainingServiceTypes = [];

        Object.values(serviceTypesForSelection).forEach((key) => {
          if (!allSelectedServiceTypes.includes(key)) {
            remainingServiceTypes.push(key);
          }
        });
        serviceTypesForSelection = [...remainingServiceTypes, ...item.serviceType];
      } else {
        serviceTypesForSelection = [...item.serviceType];
      }

      supportedChannelControl.at(idx).patchValue({ commonServiceTypes: serviceTypesForSelection });
    });
  }

  getServiceTypesList(channelGroup, i) {
    this.onChannelChange(i);
    return channelGroup.get('commonServiceTypes').value;
  }

  getTenderNamesByChannel(channelGroup) {
    let validTenderNames;
    validTenderNames = Array.from(
      new Set(this.tenderConfigurations.value.map((tender) => tender.name)),
    );

    const updatedChannelGroupValue = { ...channelGroup?.value };
    if (updatedChannelGroupValue?.tenderName) {
      updatedChannelGroupValue.tenderName = channelGroup?.value?.tenderName?.filter((tender) =>
        validTenderNames.includes(tender),
      );
    }

    channelGroup.patchValue(updatedChannelGroupValue);
    return validTenderNames;
  }

  getAdapters() {
    this.brandId = sessionStorage.getItem('brand_id');
    this.isLoading = true;
    this.adapters = [];
    this.paymentConfigService.getAllTemplates(this.brandId).subscribe({
      next: (templates) => {
        this.adapters = Array.from(new Set(templates.map((t) => t.adapter)));
        this.isLoading = false;

        this.setFormData();
      },
      error: () => {
        this.isLoading = false;
        this.loadTemplateError = true;
      },
    });
  }

  onTemplateSelect(template) {
    template.template = this.filterTemplateByStoreChannels(template.template);
    this.updateData(template);
    this.form.markAsDirty();
  }

  filterTemplateByStoreChannels(template: any): any {
    if (!template.supportedChannel || !this.channelsList) {
      this.invalidTemplateError = false;
      return template;
    }

    const filteredChannels: any = {};
    for (const key in template.supportedChannel) {
      const [channel, rawServiceType] = key.split(' ');

      if (rawServiceType) {
        const serviceType = rawServiceType.slice(1, -1);
        if (this.channelsList[channel] && this.channelsList[channel].includes(serviceType)) {
          filteredChannels[key] = template.supportedChannel[key];
        }
      }
    }
    return { ...template, supportedChannel: filteredChannels };
  }

  setFormData() {
    if (this.adapters.length) {
      if (this.storeTemplate) {
        this.getTemplates().subscribe((data) => {
          this.storeTemplates = data;

          // Filter to only consumer-api aggregators for store template
          const consumerApiAggregators = this.store.aggregators?.filter(
            (agg) => agg.aggregatorId === 'consumer-api',
          );

          this.templateResponse = this.formatStoreAggregatorsToTemplate(consumerApiAggregators);

          if (this.templateResponse) {
            this.updateData(this.templateResponse);
          } else {
            this.tenderConfigurations.push(this.createTenderTypeGroup());
            this.supportedChannel.push(this.createTenderConfigGroup());
          }
        });
      } else {
        this.route?.params.pipe(takeUntil(this.unsubscribe)).subscribe((template) => {
          this.templateId = template?.templateId;
          const state = this.router.getCurrentNavigation()?.extras?.state;

          if (this.templateId && this.templateId !== 'newTenderTemplate') {
            if (state?.data) {
              this.templateResponse = state.data as TemplateResponse;
              this.updateData(this.templateResponse);
            } else {
              this.getTemplates().subscribe((data) => {
                this.templateResponse = data.length
                  ? data?.find((t) => t.templateId === this.templateId)
                  : undefined;
                this.updateData(this.templateResponse);
              });
            }
          } else {
            this.tenderConfigurations.push(this.createTenderTypeGroup());
            this.supportedChannel.push(this.createTenderConfigGroup());
          }
        });
      }
    }
  }

  getTemplates(): Observable<any> {
    this.isLoading = true;
    return this.templatesService.getAllTemplates(this.brandId, TemplateType.TenderConfig).pipe(
      map((data) => {
        return data;
      }),
      catchError(() => {
        this.isLoading = false;
        this.loadTemplateError = true;
        return of([]);
      }),
    );
  }

  createTenderTypeGroup(): UntypedFormGroup {
    let group = this.formBuilder.group({
      name: ['', Validators.required],
      tenderType: ['', Validators.required],
      tenderId: ['', Validators.required],
      adapter: [null],
      paymentNetworks: [[], Validators.required],
      isAllSelected: [true],
      zeroAmountTenderId: [null],
    });

    return group;
  }

  requiresPaymentNetworks(tenderType: TenderType): boolean {
    return !this.tenderTypesWithoutPaymentNetworks.includes(tenderType);
  }

  createTenderConfigGroup(): UntypedFormGroup {
    let group = this.formBuilder.group({
      channel: ['consumer-api', Validators.required],
      serviceType: ['', Validators.required],
      tenderName: ['', Validators.required],
      commonServiceTypes: [''],
      paymentLocations: this.formBuilder.group(
        {
          online: [true],
          atStore: [false],
          onDelivery: [false],
        },
        { validators: this.atLeastOnePaymentLocationSelected() },
      ),
    });

    return group;
  }

  atLeastOnePaymentLocationSelected(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const value = group.value;
      const isAnySelected = Object.values(value).some((selected) => selected);

      return isAnySelected ? null : { noPaymentLocationSelected: true };
    };
  }

  adapterValidator(required: boolean): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (required && !control.value) {
        return { required: true };
      }
      return null;
    };
  }

  addTenderTypeRow(): void {
    this.form.markAsDirty();
    return this.tenderConfigurations.push(this.createTenderTypeGroup());
  }

  addTenderConfigRow(): void {
    this.form.markAsDirty();
    return this.supportedChannel.push(this.createTenderConfigGroup());
  }

  deleteTenderTypeRow(i) {
    this.tenderConfigurations.removeAt(i);
    this.form.markAsDirty();
    if (i === this.selectedZeroTenderIndex) {
      this.selectedZeroTenderIndex = undefined;
    } else if (this.selectedZeroTenderIndex !== undefined) {
      this.selectedZeroTenderIndex--;
    }
  }

  deleteTenderConfigRow(i) {
    this.supportedChannel.removeAt(i);
    this.form.markAsDirty();
  }

  onTenderNameEdit(index: number): void {
    this.tenderNameBeforeEdit = this.tenderConfigurations.at(index).get('name').value;
  }

  onTenderNameChange(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    const newValue = inputElement.value;

    // Update supportedChannel if the previous tender value exists
    if (this.isTenderNameInSupportedChannel(this.tenderNameBeforeEdit)) {
      this.updateSupportedChannel(this.tenderNameBeforeEdit, newValue);
    }
  }

  isTenderNameInSupportedChannel(tenderName: string): boolean {
    return this.supportedChannel.controls.some((control) => {
      const tenderNameArray = control.get('tenderName').value;
      return tenderNameArray.includes(tenderName);
    });
  }

  updateSupportedChannel(oldValue: string, newValue: string): void {
    this.supportedChannel.controls.forEach((control) => {
      const tenderNameControl = control.get('tenderName');
      const updatedValues =
        tenderNameControl?.value &&
        tenderNameControl?.value?.map((value: string) => (value === oldValue ? newValue : value));

      tenderNameControl.setValue(updatedValues);
    });
  }

  updateData(template: GenericTemplate): void {
    this.reset();
    this.isLoading = true;
    const tenderConfigurations = template?.template?.tenderConfigurations;

    if (tenderConfigurations?.length) {
      this.form.get('name')?.patchValue(template.name);
      this.tenderConfigurations.clear();

      tenderConfigurations.forEach((config: TenderConfiguration, index: number) => {
        const configGroup = this.createTenderTypeGroup();
        configGroup.patchValue(config);

        // Handle payment networks validation based on tender type
        const tenderType = config.tenderType;
        const paymentNetworksControl = configGroup.get('paymentNetworks');

        if (!this.requiresPaymentNetworks(tenderType)) {
          paymentNetworksControl.clearValidators();
          paymentNetworksControl.setValue([]);
          paymentNetworksControl.updateValueAndValidity();
          configGroup.get('isAllSelected').setValue(false);
        } else {
          const isAllSelected = configGroup?.get('paymentNetworks')?.value.includes('All') || false;
          paymentNetworksControl.setValidators(Validators.required);
          paymentNetworksControl.updateValueAndValidity();
          configGroup.get('isAllSelected').setValue(isAllSelected);
        }

        this.tenderConfigurations.push(configGroup);
        if (config.zeroAmountTenderId !== undefined && config.zeroAmountTenderId !== null) {
          this.selectedZeroTenderIndex = index;
        }
      });
    } else {
      this.form.get('name')?.reset();
      this.addTenderTypeRow();
    }

    const supportedChannel = template?.template?.supportedChannel;
    if (supportedChannel && Object.keys(supportedChannel).length) {
      const supportedChannelData = this.savedChannelDataToFormData(
        template?.template?.supportedChannel,
      );

      if (supportedChannelData) {
        this.supportedChannel.clear();
        for (const item of supportedChannelData) {
          const channelGroup = this.createTenderConfigGroup();
          const { paymentLocations } = item;
          item.paymentLocations = Object.entries(paymentLocations).reduce((acc, [key, value]) => {
            acc[key] = value;
            return acc;
          }, {});
          channelGroup.patchValue(item);
          this.supportedChannel.push(channelGroup);
        }
      }
    } else {
      !this.supportedChannel?.length && this.supportedChannel.push(this.createTenderConfigGroup());
    }

    this.supportedChannel?.value?.forEach((channel, index) => this.onChannelChange(index));
    this.form.markAsPristine();
    if (this.emptyPaymentLocations) {
      this.form.markAsDirty();
    }
    this.isLoading = false;
  }

  savedChannelDataToFormData(channelData): TenderTypeRow[] {
    const aggregatedData: TenderTypeRow = {} as TenderTypeRow;

    for (const key in channelData) {
      const [channel, serviceType] = key.split(' [');
      const tenderName = channelData[key].tenderName;
      const keyForAggregation = `${channel} [${tenderName}]`;
      if (!aggregatedData[keyForAggregation]) {
        const savedPaymentLocations = channelData[key]?.paymentLocations || [];
        this.emptyPaymentLocations = savedPaymentLocations.length === 0;

        const paymentLocations = Object.fromEntries(
          ['online', 'atStore', 'onDelivery'].map((key) => [
            key,
            this.emptyPaymentLocations ? key === 'online' : savedPaymentLocations.includes(key),
          ]),
        );

        aggregatedData[keyForAggregation] = {
          channel: channel,
          serviceType: [serviceType?.slice(0, -1)],
          tenderName,
          paymentLocations,
        };
      } else {
        aggregatedData[keyForAggregation].serviceType.push(serviceType?.slice(0, -1));
      }
    }

    return Object.values(aggregatedData).map((entry: TenderTypeRow) => ({
      ...entry,
      serviceType: Array.from(new Set(entry.serviceType)), // Ensure unique service types
    }));
  }

  onSelectionChange(event: any, control): void {
    const selectedValues = event.value;

    if (selectedValues.includes('All')) {
      control.get('isAllSelected').setValue(true);
      control.get('paymentNetworks')?.setValue(['All']);
    } else {
      control.get('isAllSelected').setValue(false);
    }
  }

  handleCancel() {
    if (this.storeTemplate) {
      const currentUrl = this.router.url;
      this.router.navigate(['../'], { relativeTo: this.route })?.then(() => {
        this.showError = false;
        this.router.navigate([currentUrl]);
      });
    } else {
      this.router.navigate(['../..'], { relativeTo: this.route });
    }
  }

  handleSave() {
    this.reset();
    this.form.markAllAsTouched();
    if (!this.form.valid) {
      const invalidControls = this.getInvalidControls(this.form);
      this.invalidFormError = true;
      return;
    }
    const formData = DeepCloneService.clone(this.form.value);
    this.duplicateTenderNameError = this.checkDuplicateNames(formData);

    if (this.duplicateTenderNameError) {
      return;
    }

    if (this.storeTemplate) {
      this.saveStoreTemplate();
      return;
    }

    let saveData: TemplateCreateRequest;

    if (formData.template.supportedChannel) {
      saveData = this.formDataToSaveData(formData);
    } else {
      saveData = formData;
    }

    formData.template?.tenderConfigurations.forEach(
      (tenderConfig: TenderConfiguration, index: number) => {
        if (this.selectedZeroTenderIndex !== undefined && index === this.selectedZeroTenderIndex) {
          tenderConfig.zeroAmountTenderId = tenderConfig.tenderId;
        } else {
          tenderConfig.zeroAmountTenderId = undefined;
        }
      },
    );

    let saveObs: Observable<any>;
    this.isSaving = true;

    if (this.templateId && this.templateId !== 'newTenderTemplate') {
      saveObs = this.templatesService.updateTemplate(this.brandId, {
        templateType: TemplateType.TenderConfig,
        templateId: this.templateId,
        TemplateCreateRequest: saveData,
      });
    } else {
      saveObs = this.templatesService.createTemplate(this.brandId, {
        templateType: TemplateType.TenderConfig,
        TemplateCreateRequest: saveData,
      });
    }

    saveObs?.subscribe({
      next: () => {
        this.form.markAsPristine();
        this.isSaving = false;
        this.handleCancel();
      },
      error: () => {
        this.isSaving = false;
        this.showSaveError = true;
      },
    });
  }

  formDataToSaveData(formData): TemplateCreateRequest {
    const saveData = {
      name: formData.name,
      template: {
        tenderConfigurations: formData.template.tenderConfigurations,
        supportedChannel: {},
      },
    };

    formData.template.supportedChannel.forEach((channelData) => {
      let { channel, serviceType, tenderName, paymentLocations } = channelData;
      paymentLocations =
        paymentLocations && Object.keys(paymentLocations)?.filter((key) => paymentLocations[key]);

      serviceType.forEach((s) => {
        const key = `${channel} [${s}]`;
        if (!saveData.template.supportedChannel[key]) {
          // key doesn't exist
          saveData.template.supportedChannel[key] = {
            tenderName: Array.from(new Set(tenderName)),
            paymentLocations: paymentLocations,
          };
        } else {
          // key already exists
          saveData.template.supportedChannel[key].tenderName.push(
            ...Array.from(
              new Set(
                tenderName.filter(
                  (name) => !saveData.template.supportedChannel[key].tenderName.includes(name),
                ),
              ),
            ),
          );
          saveData.template.supportedChannel[key].paymentLocations = paymentLocations;
        }
      });
    });

    return saveData;
  }

  checkDuplicateNames(formData: any): boolean {
    const namesSet = new Set<string>();
    for (const config of formData.template.tenderConfigurations) {
      const name = config.name.trim();
      if (namesSet.has(name)) {
        return true; // Name is duplicated
      }
      namesSet.add(name);
    }

    return false; // All names are unique
  }

  saveStoreTemplate() {
    this.isSaving = true;
    let saveData = this.formatFormDataToStoreSaveData();
    this.storeService.updateAggregators(this.store.id, saveData).subscribe({
      next: () => {
        this.isSaving = false;
        this.storeResolverService.update({ aggregators: saveData });
        this.form.markAsPristine();
        this.handleCancel();
      },
      error: () => {
        this.isSaving = false;
        this.showSaveError = true;
      },
    });
  }

  formatFormDataToStoreSaveData(): AggregatorConfig[] {
    const { aggregators } = this.store;
    const tenderConfigData = this.form.value.template;

    aggregators.forEach((aggregator) => {
      if (aggregator.aggregatorId === 'consumer-api' && aggregator?.tenderConfigurations) {
        // Clear all tender configurations for consumer-api aggregators
        aggregator.tenderConfigurations = [];
      }
    });

    // Now rebuild tender configurations from the current form data
    tenderConfigData?.supportedChannel?.forEach((supportedChannel) => {
      supportedChannel.serviceType &&
        supportedChannel?.serviceType?.forEach((serviceType) => {
          const aggregator = aggregators?.find(
            (a) => a.aggregatorId === supportedChannel.channel && a.serviceType === serviceType,
          );

          supportedChannel.tenderName.forEach((tenderName, index) => {
            if (aggregator) {
              let zeroTenderId;
              if (
                this.selectedZeroTenderIndex !== undefined &&
                this.selectedZeroTenderIndex !== null &&
                index === this.selectedZeroTenderIndex
              ) {
                zeroTenderId = tenderConfigData.tenderConfigurations.find(
                  (tc) => tc.name === tenderName,
                )?.tenderId;
              } else {
                zeroTenderId = undefined;
              }
              if (!aggregator?.tenderConfigurations) {
                aggregator.tenderConfigurations = [];
              }
              aggregator.tenderConfigurations.push({
                name: tenderName,
                tenderType: tenderConfigData.tenderConfigurations.find(
                  (tc) => tc.name === tenderName,
                )?.tenderType,
                tenderId: tenderConfigData.tenderConfigurations.find((tc) => tc.name === tenderName)
                  ?.tenderId,
                adapter: tenderConfigData.tenderConfigurations.find((tc) => tc.name === tenderName)
                  ?.adapter,
                paymentNetworks: tenderConfigData.tenderConfigurations.find(
                  (tc) => tc.name === tenderName,
                )?.paymentNetworks,
                isAllSelected: tenderConfigData.tenderConfigurations.find(
                  (tc) => tc.name === tenderName,
                )?.isAllSelected,
                zeroAmountTenderId: zeroTenderId,
                paymentLocations: Object.keys(supportedChannel.paymentLocations).filter(
                  (key) => supportedChannel.paymentLocations[key],
                ),
              });
            }
          });
        });
    });

    return aggregators;
  }

  onTenderTypeChange(config, event) {
    const tenderType = event.value;
    const paymentNetworksControl = config.get('paymentNetworks');

    if (!this.requiresPaymentNetworks(tenderType)) {
      paymentNetworksControl.clearValidators();
      paymentNetworksControl.setValue([]);
      paymentNetworksControl.updateValueAndValidity();
      config.get('isAllSelected').setValue(false);
      if (tenderType === TenderType.Cash) {
        config.get('adapter').setValue('');
      }
    } else {
      // Keep selection explicit: user must choose a network value.
      paymentNetworksControl.setValidators(Validators.required);
      if (!Array.isArray(paymentNetworksControl.value)) {
        paymentNetworksControl.setValue([]);
      }
      paymentNetworksControl.updateValueAndValidity();
      const isAllSelected = paymentNetworksControl?.value?.includes('All') || false;
      config.get('isAllSelected').setValue(isAllSelected);
    }
  }

  reset() {
    this.loadTemplateError = false;
    this.invalidFormError = false;
    this.showSaveError = false;
    this.isSaving = false;
    this.duplicateTenderNameError = false;
    this.showLoadError = false;
  }

  getTenderTypes(): { label: string; value: string }[] {
    return [
      {
        label: 'store.channels.none',
        value: TenderType.None,
      },
      {
        label: 'store.channels.applePay',
        value: TenderType.ApplePay,
      },
      {
        label: 'store.channels.cash',
        value: TenderType.Cash,
      },
      {
        label: 'store.channels.creditCard',
        value: TenderType.CreditCard,
      },
      {
        label: 'store.channels.debitCard',
        value: TenderType.DebitCard,
      },
      {
        label: 'store.channels.giftCard',
        value: TenderType.GiftCard,
      },
      {
        label: 'store.channels.googlePay',
        value: TenderType.GooglePay,
      },
      {
        label: 'store.channels.other',
        value: TenderType.Other,
      },
    ];
  }

  setZeroAmountTenderIndex($event, index) {
    if ($event.checked === true) {
      this.selectedZeroTenderIndex = index;
    } else {
      this.selectedZeroTenderIndex = undefined;
    }
    this.form.markAsDirty();
  }

  getInvalidControls(form: AbstractControl, path: string = ''): string[] {
    const invalidControls: string[] = [];

    if (form instanceof UntypedFormGroup) {
      for (const key of Object.keys(form.controls)) {
        const control = form.controls[key];
        const currentPath = path ? `${path}.${key}` : key;

        if (
          control.invalid &&
          !(control instanceof UntypedFormGroup || control instanceof UntypedFormArray)
        ) {
          invalidControls.push(currentPath);
        }

        if (control instanceof UntypedFormGroup || control instanceof UntypedFormArray) {
          invalidControls.push(...this.getInvalidControls(control, currentPath));
        }
      }
    } else if (form instanceof UntypedFormArray) {
      form.controls.forEach((control, index) => {
        const currentPath = `${path}[${index}]`;

        if (
          control.invalid &&
          !(control instanceof UntypedFormGroup || control instanceof UntypedFormArray)
        ) {
          invalidControls.push(currentPath);
        }

        if (control instanceof UntypedFormGroup || control instanceof UntypedFormArray) {
          invalidControls.push(...this.getInvalidControls(control, currentPath));
        }
      });
    }

    return invalidControls;
  }
}
